// User Management API module
import { BaseApiClient } from './BaseApiClient';
import type {
  ApiResponse,
  UsersApiResponse,
} from '@/types/api';
import type {
  MediaHubUser,
  UserFilters,
  PaginationParams,
  AdminUserAction,
  ExportOptions,
  UserLibraryItem,
  UserStatus,
  UserUsageStats,
} from '@/types/admin';

interface RequestErrorShape {
  message?: string;
  response?: {
    status?: number;
    data?: unknown;
  };
  config?: {
    url?: string;
  };
}

interface UserPaymentHistoryResponse {
  items?: unknown[];
  total_count?: number;
  [key: string]: unknown;
}

export class UserManagementApi extends BaseApiClient {
  constructor() {
    super('UserManagementAPI');
  }

  async getUsers(
    filters?: UserFilters,
    pagination?: PaginationParams
  ): Promise<UsersApiResponse<MediaHubUser>> {
    // Validate and sanitize parameters to prevent 422 errors
    const validatedPagination = pagination ? {
      page: Math.max(1, Math.floor(pagination.page || 1)),
      // Backend allows max=500 (see /api/v1/admin/users docs + FastAPI Query(le=500))
      limit: Math.min(500, Math.max(1, Math.floor(pagination.limit || 20))),
      // Optional server-side sorting (backend validates allowed values)
      ...(pagination.sort_by ? { sort_by: String(pagination.sort_by) } : {}),
      ...(pagination.sort_dir ? { sort_dir: pagination.sort_dir } : {}),
      ...(pagination.pagination_mode ? { pagination_mode: pagination.pagination_mode } : {}),
      ...(pagination.cursor ? { cursor: pagination.cursor } : {}),
    } : { page: 1, limit: 20 };

    // Clean up filters - remove undefined/empty values
    const cleanFilters = filters ? Object.fromEntries(
      Object.entries(filters).filter(([_, value]) =>
        value !== undefined && value !== null && value !== ''
      )
    ) : {};

    const params = {
      ...cleanFilters,
      ...validatedPagination,
    };

    try {
      const response = await this.api.get<UsersApiResponse<MediaHubUser>>('/users', { params });

      return response.data;
    } catch (error: unknown) {
      const requestError = (typeof error === 'object' && error !== null ? error : {}) as RequestErrorShape;
      // Enhanced error handling for users endpoint
      if (requestError.response?.status === 422) {
        this.logger.warn('Users endpoint returned 422:', {
          params,
          cleanedParams: params,
          originalFilters: filters,
          originalPagination: pagination,
          error: requestError.response?.data,
          message: 'The server could not process the request. This may indicate the API endpoint expects different parameters.'
        });

        // Try a minimal request to see if the endpoint works at all
        try {
          this.logger.debug('Attempting minimal request to diagnose 422 error...');
          await this.api.get<UsersApiResponse<MediaHubUser>>('/users?limit=1');
          this.logger.debug('Minimal request succeeded - issue is with specific parameters');
        } catch (minimalError) {
          this.logger.debug('Even minimal request failed:', minimalError);
        }
      }
      throw error;
    }
  }

  async getUserLanguageStats(filters?: UserFilters): Promise<{
    total: number;
    counts_by_language: Record<string, number>;
  }> {
    // Clean up filters - remove undefined/empty values
    const cleanFilters = filters
      ? Object.fromEntries(
          Object.entries(filters).filter(
            ([_, value]) => value !== undefined && value !== null && value !== ''
          )
        )
      : {};

    const params = {
      ...cleanFilters,
    };

    const response = await this.api.get('/users/language-stats', { params });
    return response.data;
  }

  async getUserStats(options?: { days?: number; force_refresh?: boolean }): Promise<{
    users: { new_users: number };
    period_days: number;
    generated_at: string;
  }> {
    const params = {
      days: options?.days ?? 7,
      ...(options?.force_refresh ? { force_refresh: true } : {}),
    };
    const response = await this.api.get('/users/stats', { params });
    return response.data;
  }

  async getUser(userId: string): Promise<ApiResponse<MediaHubUser>> {
    const response = await this.api.get<ApiResponse<MediaHubUser>>(`/users/${userId}`);
    return response.data;
  }

  async updateUser(userId: string, updates: Partial<MediaHubUser>): Promise<ApiResponse<MediaHubUser>> {
    const response = await this.api.put<ApiResponse<MediaHubUser>>(`/users/${userId}`, updates);
    return response.data;
  }

  async performUserAction(userId: string, action: AdminUserAction): Promise<ApiResponse<void>> {
    const response = await this.api.post<ApiResponse<void>>(`/users/${userId}/actions`, action);
    return response.data;
  }

  async exportUsers(options: ExportOptions): Promise<ApiResponse<{ download_url: string }>> {
    // Use GET method as per API documentation: GET /api/v1/admin/users/export
    // Pass options as query parameters instead of request body
    const params = {
      format: options.format || 'json',
      ...(options.filters && Object.keys(options.filters).length > 0 && options.filters),
      ...(options.include_usage_stats !== undefined && { include_usage_stats: options.include_usage_stats })
    };

    const response = await this.api.get<ApiResponse<{ download_url: string }>>('/users/export', { params });
    return response.data;
  }

  async getUserLibrary(userId: string): Promise<{ items: UserLibraryItem[]; total_count: number }> {
    const response = await this.api.get<{ success: boolean; user_id: string; items: UserLibraryItem[]; total_count: number }>(
      `/users/${userId}/library`
    );
    return response.data;
  }

  async deleteUser(userId: string, reason?: string, deleteFiles?: boolean): Promise<ApiResponse<void>> {
    // Validate user ID parameter
    if (!userId || typeof userId !== 'string') {
      throw new Error('Invalid user ID parameter');
    }

    const params = new URLSearchParams();
    if (reason) {
      params.append('reason', reason);
    }
    if (deleteFiles) {
      params.append('delete_files', 'true');
    }

    const url = `/users/${userId}${params.toString() ? `?${params.toString()}` : ''}`;
    this.logger.info(`Attempting to permanently delete user ${userId}`, { reason });

    try {
      // Try the DELETE endpoint as documented - this should permanently remove the user
      const response = await this.api.delete<ApiResponse<void>>(url);
      this.logger.info(`User ${userId} permanently deleted successfully`);
      return response.data;
    } catch (error: unknown) {
      const requestError = (typeof error === 'object' && error !== null ? error : {}) as RequestErrorShape;
      this.logger.error(`DELETE endpoint failed for user ${userId}:`, {
        status: requestError.response?.status,
        message: requestError.message,
        url: requestError.config?.url
      });

      // If DELETE fails, fall back to status marking (previous implementation)
      this.logger.warn(`Falling back to status marking for user ${userId}`);
      const fallbackResponse = await this.updateUser(userId, {
        status: 'deleted' as UserStatus
      });

      this.logger.info(`User ${userId} marked as deleted (fallback method)`);
      return {
        data: undefined,
        message: 'User marked as deleted (fallback)',
        status: fallbackResponse.status ?? 'success',
        timestamp: fallbackResponse.timestamp ?? new Date().toISOString(),
      };
    }
  }

  async getUserPaymentHistory(
    userId: string,
    pagination?: PaginationParams
  ): Promise<UserPaymentHistoryResponse> {
    const params = pagination || {};
    const response = await this.api.get<UserPaymentHistoryResponse>(`/users/${userId}/payment-history`, { params });
    return response.data;
  }

  async getUserUsageStats(
    userId: string,
    dateRange?: { start_date?: string; end_date?: string }
  ): Promise<ApiResponse<UserUsageStats>> {
    const params = dateRange ? { ...dateRange } : {};
    const response = await this.api.get<ApiResponse<UserUsageStats>>(`/users/${userId}/usage-stats`, { params });
    return response.data;
  }
}

